import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import useGeoBootstrap from '../hooks/useGeoBootstrap';
import useGeoFilters from '../hooks/useGeoFilters';
import useGeoQueries from '../hooks/useGeoQueries';
import GeoFiltersDrawer from '../components/GeoFiltersDrawer';
import GeoEditPromptsModal from '../components/GeoEditPromptsModal';
import GeoPromptMetricsCells from '../components/GeoPromptMetricsCells';
import GeoTableHeaderLabel from '../components/GeoTableHeaderLabel';
import {
  DEFAULT_GEO_PROMPTS_PAGE,
  DEFAULT_GEO_PROMPTS_PAGE_SIZE,
  GEO_PAGE_TITLE,
  GEO_PROMPTS_PAGE_SIZE_OPTIONS,
} from '../utils/geoConstants';

const PROMPTS_TABLE_COLS = 5;

export default function GeoPrompts() {
  const { loading: bootstrapLoading } = useGeoBootstrap();
  const [currentPage, setCurrentPage] = useState(DEFAULT_GEO_PROMPTS_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_GEO_PROMPTS_PAGE_SIZE);
  const [pageInput, setPageInput] = useState('1');
  const [openFilter, setOpenFilter] = useState(null);
  const [showEditPrompts, setShowEditPrompts] = useState(false);
  const [queriesRefreshKey, setQueriesRefreshKey] = useState(0);

  const filters = useGeoFilters({
    mode: 'prompts',
    promptsPage: currentPage,
    promptsPageSize: rowsPerPage,
  });

  const {
    searchTerm,
    setSearchTerm,
    showTableFilters,
    setShowTableFilters,
    hasFiltersApplied,
    handleClearFilters,
    applyFilters,
    promptsParams,
  } = filters;

  const {
    queries,
    total,
    loading: queriesLoading,
    error,
  } = useGeoQueries({
    promptsParams,
    enabled: !bootstrapLoading,
    refreshKey: queriesRefreshKey,
  });

  const loading = bootstrapLoading || queriesLoading;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage) || 1);
  const safePage = total === 0 ? 1 : Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  const handleDrawerApply = useCallback((next) => {
    setCurrentPage(DEFAULT_GEO_PROMPTS_PAGE);
    applyFilters(next);
  }, [applyFilters]);

  useEffect(() => {
    if (currentPage > totalPages && total > 0 && !loading) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages, total, loading]);

  useEffect(() => {
    setPageInput(String(safePage));
  }, [safePage]);

  const handleClearAllFilters = () => {
    handleClearFilters();
    setCurrentPage(DEFAULT_GEO_PROMPTS_PAGE);
    setRowsPerPage(DEFAULT_GEO_PROMPTS_PAGE_SIZE);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const parsed = parseInt(pageInput, 10);
    if (!Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      setCurrentPage(parsed);
    } else {
      setPageInput(String(safePage));
    }
  };

  const rangeStart = total === 0 ? 0 : (safePage - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(safePage * rowsPerPage, total);

  const animationSourceKey = useMemo(() => {
    const { page, ...filterParams } = promptsParams;
    return `${queriesRefreshKey}:${JSON.stringify(filterParams)}`;
  }, [queriesRefreshKey, promptsParams]);
  const [contentAnimationKey, setContentAnimationKey] = useState(1);
  const lastAnimatedSourceRef = useRef(null);
  const prevLoadingRef = useRef(loading);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    const fetchJustCompleted = wasLoading && !loading;
    if (!fetchJustCompleted) return;
    if (lastAnimatedSourceRef.current === animationSourceKey) return;

    lastAnimatedSourceRef.current = animationSourceKey;
    setContentAnimationKey((key) => key + 1);
  }, [loading, animationSourceKey]);

  const promptsTableBody = (
    <>
      <div className="table-wrap">
        <table className="sec-filing-insights-data-table geo-insights-data-table">
          <thead>
            <tr>
              <th className="col-sno">S.No</th>
              <th className="col-prompt">Prompt</th>
              <th className="col-coverage">
                <GeoTableHeaderLabel metricKey="coverage_pct">Coverage %</GeoTableHeaderLabel>
              </th>
              <th className="col-position">
                <GeoTableHeaderLabel metricKey="avg_position">Avg Position</GeoTableHeaderLabel>
              </th>
              <th className="col-sentiment">
                <GeoTableHeaderLabel metricKey="overall_sentiment">Overall sentiment</GeoTableHeaderLabel>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={PROMPTS_TABLE_COLS}>Loading prompts…</td>
              </tr>
            ) : queries.length === 0 ? (
              <tr>
                <td colSpan={PROMPTS_TABLE_COLS}>No prompts match your filters.</td>
              </tr>
            ) : (
              queries.map((row, index) => (
                <tr key={row.id} style={{ '--table-row-delay': index }}>
                  <td className="col-sno">{(safePage - 1) * rowsPerPage + index + 1}</td>
                  <td className="col-prompt geo-prompt-cell">{row.query}</td>
                  <GeoPromptMetricsCells row={row} />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination pagination-sticky">
        <div className="pg-info">
          Showing {rangeStart}–{rangeEnd} of {total} prompts
        </div>
        <div className="table-pagination">
          <div className="table-pagination__control">
            <span className="table-pagination__label">Rows per page</span>
            <div className={`select-wrap ${openFilter === 'rows' ? 'is-open' : ''}`}>
              <span className="select-measure table-pagination__measure">{rowsPerPage}</span>
              <select
                className="filter-select table-pagination__select"
                value={rowsPerPage}
                onFocus={() => setOpenFilter('rows')}
                onBlur={() => setOpenFilter(null)}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                  e.target.blur();
                }}
              >
                {GEO_PROMPTS_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="pg-controls">
            <button
              type="button"
              className="pg-btn"
              disabled={safePage === 1 || total === 0}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} />
            </button>
            <form onSubmit={handlePageInputSubmit} className="companydetails-style-33">
              <input
                type="text"
                className="pg-input"
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={() => setPageInput(String(safePage))}
              />
              <button type="submit" style={{ display: 'none' }} />
            </form>
            <button
              type="button"
              className="pg-btn"
              disabled={safePage === totalPages || total === 0}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <PageHeader
        title={GEO_PAGE_TITLE}
        activePageTab="Table"
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search prompt id or query text..."
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={handleClearAllFilters}
        showTableFilters={showTableFilters}
        onToggleFilters={() => setShowTableFilters((open) => !open)}
        onEditPrompts={() => setShowEditPrompts(true)}
      />
      <div className="body">
        <div className="table-area geo-analytics-panel filings-table-panel geo-prompts-table-panel">
          {error ? (
            <p className="assistants-status geo-insights-error" role="alert">
              {error}
              {' '}
              — ensure Prompt Pulse is available.
            </p>
          ) : null}
          <div key={contentAnimationKey} className="geo-prompts-content">
            {promptsTableBody}
          </div>
        </div>
      </div>

      <GeoFiltersDrawer
        isOpen={showTableFilters}
        onClose={() => setShowTableFilters(false)}
        filters={filters}
        onApply={handleDrawerApply}
      />

      <GeoEditPromptsModal
        isOpen={showEditPrompts}
        onClose={() => setShowEditPrompts(false)}
        onSaved={() => setQueriesRefreshKey((k) => k + 1)}
      />
    </>
  );
}
